import { FunctionComponent, useState, useEffect } from "react";
import { connect } from "react-redux";
import { themeColors } from "../../theme";
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import Modal, { Styles } from "react-modal";
import ColorPicker from "react-pick-color";
import { fetchpropertyType } from "../../../Services/contacts";
import StyledCancelButton from "../../Buttons/StyledCancelButton";
import StyledSubmitButton from "../../Buttons/StyledSubmitButton";
import InputTextValue from "../../TextInput/InputTextValue";
import DropDownText from "../../../Components/DropDownText/DropDownText";


/* ------------------------------------------------------------------
   PROPS
-------------------------------------------------------------------*/
type props = {
  open: boolean;
  close: () => void;
  heading: string;
  title: string;
  onSave: (v: any, c: any) => void;
  theme: any;
  user: any;
  conatctHeader: any;
};

/* ------------------------------------------------------------------
   MAIN COMPONENT
-------------------------------------------------------------------*/
const AddResources: FunctionComponent<props> = ({
  open,
  close,
  heading,
  title,
  onSave,
  theme,
  user,
  conatctHeader,
}) => {

  /* ---------------------------------------------
   THEME + RESPONSIVENESS
----------------------------------------------*/
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));
  const [value, setValue] = useState("");
  const [color, setColor] = useState("#262944");
  const [propertyTypeList, setPropertyTypeList] = useState<{ label: string; value: string }[]>([]);
  const [propertyTypeSelected, setPropertyTypeSelected] = useState<any>({ label: "Select", value: "Select" });

  /* ---------------------------------------------
   FETCH PROPERTY TYPES (ORG-SPECIFIC)
----------------------------------------------*/
  useEffect(() => {
    if (user.user_email === "info@leadsrubix.com") {
      const unsubProperty = fetchpropertyType(
        " ",
        (data) => {
          const formatted = Array.isArray(data)
            ? data.map((item: any) =>
              typeof item === "string"
                ? { label: item, value: item }
                : item
            )
            : [];
          setPropertyTypeList(formatted);
        }
      );
      return () => {
        unsubProperty();
      };
    }
    else {
      const unsubProperty = fetchpropertyType(
        user.organization_id,
        (data) => {
          const formatted = Array.isArray(data)
            ? data.map((item: any) =>
              typeof item === "string"
                ? { label: item, value: item }
                : item
            )
            : [];
          setPropertyTypeList(formatted);
        }
      );
      return () => {
        unsubProperty();
      };
    }
  }, []);

  /* ---------------------------------------------
   MODAL STYLE
----------------------------------------------*/
  const modalStyle: Styles = {
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)", // darkens the background
      zIndex: 1300, // higher than MUI AppBar/Table
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    content: {
      background: theme
        ? themeColors.cardBackgroundColor
        : themeColors.cardBackgroundColorDark,
      color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
      borderRadius: 16,
      padding: isMobile ? "24px 12px" : "40px 32px",
      maxWidth: 380,
      minWidth: 320,
      border: "none",
      boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      position: "relative",
      inset: "auto",
      margin: "auto",
    },
  };

  /* ------------------------------------------------------------------
   RENDER
-------------------------------------------------------------------*/
  return (
    <Modal
      isOpen={open}
      onRequestClose={close}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      style={modalStyle}
      ariaHideApp={false}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 340,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <form style={{ width: "100%" }}>

          {/* --------------------------------------------- */}
          {/* MODAL HEADER */}
          {/* --------------------------------------------- */}
          <Box sx={{ mb: 2 }}>
            <h3
              style={{
                color: theme ? "#272944" : "#fff",
                fontFamily: "var(--font-family-main)",
                fontSize: "var(--font-size-large)",
                marginBottom: 24,
                textAlign: "center",
                fontWeight: 700,
                letterSpacing: 0.5,
              }}
            >
              {heading}
            </h3>
          </Box>

          {/* --------------------------------------------- */}
          {/* INPUT FIELDS */}
          {/* --------------------------------------------- */}
          <Grid container spacing={2} sx={{ width: "100%" }}>
             {/* Text Field */}
            <Grid item xs={12}>
              <InputTextValue
                title={heading}
                value={value}
                onChange={(event) => setValue(event.target.value)}
              />
            </Grid>
            
             {/* Color Picker (Only for Lead Source) */}
            <Grid item xs={12}>
              {heading === "Lead Source" && (
                <>
                  <Box sx={{ pl: isMobile ? 3 : 1.5 }}>
                    <ColorPicker color={color} onChange={(color) => setColor(color.hex)} />
                  </Box>
                </>
              )}
            </Grid>

             {/* Property Sub Type → Dropdown */}
            <Grid item xs={12}>
              {heading === "Property Sub Type" && (
                <DropDownText
                  title={`${conatctHeader.find((obj: any) => obj.label === "property_type").value}`}
                  options={propertyTypeList}
                  value={propertyTypeSelected?.value === "Select" ? "" : propertyTypeSelected?.value}
                  onChange={(e: any) => setPropertyTypeSelected({ value: e.target.value, label: e.target.value })}
                  labelKey="label"
                  valueKey="value"
                />
              )}
            </Grid>
          </Grid>

          {/* --------------------------------------------- */}
          {/* BUTTONS: Cancel + Save */}
          {/* --------------------------------------------- */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 4,
                width: "100%",
              }}
            >
              <Box style={{ marginRight: 8 }}>
                <StyledCancelButton onClick={close} isMobile={isMobile}>
                  Cancel
                </StyledCancelButton>
              </Box>
              <Box>
                {heading !== "Property Sub Type" ? (
                  <StyledSubmitButton
                    onClick={() => onSave(value, color)}
                    themeMode={themeMUI.palette.mode}
                    isMobile={isMobile}
                  >
                    Save
                  </StyledSubmitButton>
                ) : (
                  <StyledSubmitButton
                    onClick={() => onSave(value, propertyTypeSelected)}
                    themeMode={themeMUI.palette.mode}
                    isMobile={isMobile}
                  >
                    Save
                  </StyledSubmitButton>
                )}
              </Box>
            </Box>
          </Grid>
        </form>
      </Box>
    </Modal >
  );
};

/* ------------------------------------------------------------------
   REDUX
-------------------------------------------------------------------*/
const mapStateToProps = (state: any) => {
  return {
    user: state.user.data,
    contacts: state.contacts,
    theme: state.theme.isLightMode,
    conatctHeader: state.contactHeaderList.data,
  };
};

export default connect(mapStateToProps)(AddResources);

